import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type CSSProperties,
} from 'react';
import {
  isVisitorInformationFormValid,
  validateVisitorInformationForm,
  type VisitorInformationConfig,
  type VisitorInformationErrors,
  type VisitorInformationValues,
} from '../visitor-information';

type VisitorInformationFormProps = {
  config: VisitorInformationConfig;
  submitting: boolean;
  error?: string | null;
  onSubmit: (values: VisitorInformationValues) => void;
  style?: CSSProperties;
};

const EMPTY: VisitorInformationValues = {
  name: '',
  email: '',
  phone: '',
};

export function VisitorInformationForm({
  config,
  submitting,
  error,
  onSubmit,
  style,
}: VisitorInformationFormProps) {
  const [values, setValues] = useState<VisitorInformationValues>(EMPTY);
  const [touched, setTouched] = useState(false);
  const [errors, setErrors] = useState<VisitorInformationErrors>({});
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!touched) return;
    setErrors(validateVisitorInformationForm(config, values));
  }, [config, values, touched]);

  const valid = isVisitorInformationFormValid(config, values);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    const nextErrors = validateVisitorInformationForm(config, values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || submitting) return;
    onSubmit(values);
  }

  const fields: Array<{
    key: keyof VisitorInformationValues;
    label: string;
    type: string;
    autoComplete: string;
    enabled: boolean;
    required: boolean;
  }> = [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      autoComplete: 'name',
      enabled: config.fields.name.enabled,
      required: config.fields.name.required,
    },
    {
      key: 'email',
      label: 'Email',
      type: 'email',
      autoComplete: 'email',
      enabled: config.fields.email.enabled,
      required: config.fields.email.required,
    },
    {
      key: 'phone',
      label: 'Phone',
      type: 'tel',
      autoComplete: 'tel',
      enabled: config.fields.phone.enabled,
      required: config.fields.phone.required,
    },
  ];

  let firstEnabledAssigned = false;

  return (
    <form
      className="rw-visitor-form"
      onSubmit={handleSubmit}
      style={style}
      noValidate
    >
      <div className="rw-visitor-form-intro">
        <h2 className="rw-visitor-form-title">Let&apos;s get started</h2>
        <p className="rw-visitor-form-subtitle">
          Share a few details so we can help you faster.
        </p>
      </div>

      <div className="rw-visitor-form-fields">
        {fields.map((field) => {
          if (!field.enabled) return null;
          const isFirst = !firstEnabledAssigned;
          firstEnabledAssigned = true;
          return (
            <label key={field.key} className="rw-visitor-field">
              <span className="rw-visitor-label">
                {field.label}
                {field.required ? (
                  <span className="rw-visitor-required" aria-hidden>
                    {' '}
                    *
                  </span>
                ) : null}
              </span>
              <input
                ref={isFirst ? firstFieldRef : undefined}
                type={field.type}
                name={field.key}
                autoComplete={field.autoComplete}
                value={values[field.key]}
                disabled={submitting}
                aria-invalid={Boolean(errors[field.key])}
                aria-required={field.required}
                onChange={(event) => {
                  const next = {
                    ...values,
                    [field.key]: event.target.value,
                  };
                  setValues(next);
                  if (!touched) setTouched(true);
                }}
              />
              {errors[field.key] ? (
                <span className="rw-visitor-error" role="alert">
                  {errors[field.key]}
                </span>
              ) : null}
            </label>
          );
        })}
      </div>

      {error ? (
        <div className="rw-visitor-form-banner" role="alert">
          {error}
        </div>
      ) : null}

      <button
        type="submit"
        className="rw-visitor-submit"
        disabled={submitting || !valid}
      >
        {submitting ? 'Starting…' : 'Start Conversation'}
      </button>
    </form>
  );
}
